import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { API_URL } from '../lib/supabase';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Plus, Activity, Clock, Package } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      navigate('/clinic/login');
      return;
    }
    if (user.role !== 'clinic_staff') {
      navigate('/owner');
      return;
    }
    fetchCases();
  }, [user, navigate]);

  const fetchCases = async () => {
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${API_URL}/api/clinic/cases`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCases(response.data);
    } catch (error) {
      toast.error('Error al cargar los casos');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Recibido': 'bg-slate-100 text-slate-700',
      'En evaluación': 'bg-amber-100 text-amber-700',
      'En procedimiento': 'bg-amber-100 text-amber-700',
      'En recuperación': 'bg-green-100 text-green-700',
      'Listo para recoger': 'bg-green-100 text-green-700',
      'Cerrado': 'bg-slate-100 text-slate-500'
    };
    return colors[status] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Activity className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Cargando casos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Dashboard</h1>
              <p className="text-slate-600 mt-1">Gestiona los casos activos de tu clínica</p>
            </div>
            <Link to="/clinic/cases/new">
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="create-case-button">
                <Plus className="h-5 w-5 mr-2" />
                Crear Caso
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6 bg-white border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Casos Activos</p>
                <p className="text-3xl font-bold text-slate-900">{cases.length}</p>
              </div>
              <div className="bg-blue-100 w-12 h-12 rounded-lg flex items-center justify-center">
                <Package className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">En Procedimiento</p>
                <p className="text-3xl font-bold text-slate-900">
                  {cases.filter(c => c.status === 'En procedimiento').length}
                </p>
              </div>
              <div className="bg-amber-100 w-12 h-12 rounded-lg flex items-center justify-center">
                <Activity className="h-6 w-6 text-amber-600" />
              </div>
            </div>
          </Card>

          <Card className="p-6 bg-white border border-slate-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 mb-1">Listos para Recoger</p>
                <p className="text-3xl font-bold text-slate-900">
                  {cases.filter(c => c.status === 'Listo para recoger').length}
                </p>
              </div>
              <div className="bg-green-100 w-12 h-12 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Cases List */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-200">
            <h2 className="text-lg font-semibold text-slate-900">Casos Activos</h2>
          </div>
          
          {cases.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500 mb-4">No hay casos activos</p>
              <Link to="/clinic/cases/new">
                <Button variant="outline">Crear Primer Caso</Button>
              </Link>
            </div>
          ) : (
            <div className="divide-y divide-slate-200">
              {cases.map((caseItem) => (
                <Link
                  key={caseItem.id}
                  to={`/clinic/cases/${caseItem.id}`}
                  className="block px-6 py-4 hover:bg-slate-50 transition-colors"
                  data-testid={`case-item-${caseItem.id}`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-lg font-semibold text-slate-900">
                          {caseItem.pet?.name || 'Mascota'}
                        </h3>
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(caseItem.status)}`}>
                          {caseItem.status}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <span>{caseItem.pet?.species} - {caseItem.pet?.breed || 'N/A'}</span>
                        <span>•</span>
                        <span>Dueño: {caseItem.owner?.full_name}</span>
                        <span>•</span>
                        <span>{format(new Date(caseItem.created_at), "d 'de' MMMM", { locale: es })}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">Razón</p>
                      <p className="text-sm font-medium text-slate-900">{caseItem.reason}</p>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
